import { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { useTranslation } from "react-i18next";

import {
  useApi,
  ApiError,
  type SessionAssessmentRow,
  type SessionDetailResponse,
  type SessionSummary,
} from "../api/client";
import { frameworkLabel, useFramework, type DomainId } from "../framework";
import { StateIcon } from "./StateIcon";
import "./CycleHistoryStrip.css";
import { formatDate, formatDateTime } from "../api/formatDate";

/**
 * Cycle history strip on the whānau profile.
 *
 * Renders one chip per assessment_session. Click a chip → modal opens
 * showing the full detail (cycle, date, setting, all 18 recorded states
 * at that moment).
 *
 * Sessions are pre-loaded with the profile response so the strip renders
 * immediately; the per-session detail is fetched on demand when a chip
 * is clicked.
 *
 * Phase 1 cycle vocabulary: entry / midpoint / exit / follow-up. Chip
 * border colour distinguishes them.
 */

type Props = {
  whanauId: number;
  sessions: SessionSummary[];
};

export function CycleHistoryStrip({ whanauId, sessions }: Props) {
  const { t } = useTranslation();
  const [openSessionId, setOpenSessionId] = useState<number | null>(null);

  if (sessions.length === 0) {
    // Don't render anything if there are no sessions yet — the kaimahi
    // hasn't recorded a session for this whānau. Cell-edit corrections
    // (which don't have a session_id) won't show here.
    return null;
  }

  return (
    <section className="cycle-history" aria-label={t("cycleHistory.heading")}>
      <h3 className="cycle-history__heading">
        {t("cycleHistory.heading")}{" "}
        <span className="cycle-history__count">
          {t("cycleHistory.count", { count: sessions.length })}
        </span>
      </h3>
      <ol className="cycle-history__list">
        {sessions.map((s) => (
          <li key={s.session_id}>
            <button
              type="button"
              className={`cycle-chip cycle-chip--${s.cycle_label}`}
              onClick={() => setOpenSessionId(s.session_id)}
              aria-label={t("cycleHistory.openSession", {
                cycle: t(`wizard.cycle.${s.cycle_label}`, {
                  defaultValue: s.cycle_label,
                }),
                date: formatDate(s.session_at),
              })}
            >
              <span className="cycle-chip__label">
                {t(`wizard.cycle.${s.cycle_label}`, {
                  defaultValue: s.cycle_label,
                })}
              </span>
              <span className="cycle-chip__date">
                {formatDate(s.session_at)}
              </span>
              <span className="cycle-chip__count">
                {t("cycleHistory.assessmentCount", {
                  count: s.assessment_count,
                })}
              </span>
            </button>
          </li>
        ))}
      </ol>

      {openSessionId !== null && (
        <SessionDetailModal
          whanauId={whanauId}
          sessionId={openSessionId}
          onClose={() => setOpenSessionId(null)}
        />
      )}
    </section>
  );
}

// ---------------------------------------------------------------------------
// Session detail modal — opens on chip click, fetches detail, shows it.
// ---------------------------------------------------------------------------

function SessionDetailModal({
  whanauId,
  sessionId,
  onClose,
}: {
  whanauId: number;
  sessionId: number;
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const callApi = useApi();

  const [state, setState] = useState<
    | { kind: "loading" }
    | { kind: "ok"; data: SessionDetailResponse }
    | { kind: "notFound" }
    | { kind: "error"; message: string; status?: number }
  >({ kind: "loading" });

  useEffect(() => {
    let active = true;
    callApi<SessionDetailResponse>(
      `/whanau/${String(whanauId)}/assessment-sessions/${String(sessionId)}`,
    )
      .then((data) => {
        if (active) setState({ kind: "ok", data });
      })
      .catch((err: unknown) => {
        if (!active) return;
        if (err instanceof ApiError) {
          if (err.status === 404) {
            setState({ kind: "notFound" });
          } else {
            setState({
              kind: "error",
              status: err.status,
              message: err.message,
            });
          }
        } else {
          const message = err instanceof Error ? err.message : "Unknown error";
          setState({ kind: "error", message });
        }
      });
    return () => {
      active = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whanauId, sessionId]);

  // ESC / body scroll lock — same idiom as the wizard modal.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.stopPropagation();
        onClose();
      }
    };
    document.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const overlay = (
    <div
      className="session-modal-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="session-modal-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="session-modal">
        <header className="session-modal__header">
          <div>
            <h2 id="session-modal-title" className="session-modal__title">
              {state.kind === "ok"
                ? t(`wizard.cycle.${state.data.session.cycle_label}`, {
                    defaultValue: state.data.session.cycle_label,
                  })
                : t("cycleHistory.session")}
            </h2>
            {state.kind === "ok" && (
              <p className="session-modal__subtitle">
                {formatDateTime(state.data.session.session_at)}
                {state.data.session.setting && (
                  <>
                    {" · "}
                    <span>{state.data.session.setting}</span>
                  </>
                )}
              </p>
            )}
          </div>
          <button
            type="button"
            className="session-modal__close"
            onClick={onClose}
            aria-label={t("cycleHistory.close")}
          >
            ×
          </button>
        </header>

        <section className="session-modal__body">
          {state.kind === "loading" && (
            <p className="session-modal__status">{t("cycleHistory.loading")}</p>
          )}

          {state.kind === "notFound" && (
            <p className="session-modal__status session-modal__status--error">
              {t("cycleHistory.notFound")}
            </p>
          )}

          {state.kind === "error" && (
            <p className="session-modal__status session-modal__status--error">
              {state.status
                ? `${String(state.status)} — ${state.message}`
                : state.message}
            </p>
          )}

          {state.kind === "ok" && (
            <>
              {state.data.session.session_notes && (
                <div className="session-modal__notes">
                  <p className="session-modal__notes-label">
                    {t("cycleHistory.sessionNotes")}
                  </p>
                  <p className="session-modal__notes-body">
                    {state.data.session.session_notes}
                  </p>
                </div>
              )}

              <SessionFactorList assessments={state.data.assessments} />
            </>
          )}
        </section>
      </div>
    </div>
  );

  return createPortal(overlay, document.body);
}

// ---------------------------------------------------------------------------
// Read-only factor list for one session, grouped by domain.
// ---------------------------------------------------------------------------

function SessionFactorList({
  assessments,
}: {
  assessments: SessionAssessmentRow[];
}) {
  const { t, i18n } = useTranslation();
  const { domains, findSubdomain } = useFramework();

  // Look up the merged subdomain so renames apply. Historical sessions
  // intentionally retain their rows even if the subdomain was later
  // hidden — that's the conservative "no destructive delete" posture.
  const labelFor = (subdomainId: string, fallback: string): string => {
    const merged = findSubdomain(subdomainId);
    return merged
      ? frameworkLabel(merged, "subdomain", i18n.language, t)
      : fallback;
  };

  const byDomain = new Map<DomainId, SessionAssessmentRow[]>();
  for (const d of domains) byDomain.set(d.id, []);
  for (const a of assessments) {
    const list = byDomain.get(a.domain_id as DomainId);
    if (list) list.push(a);
  }

  return (
    <div className="session-factor-list">
      {domains.map((domain) => {
        const rows = byDomain.get(domain.id) ?? [];
        if (rows.length === 0) return null;
        return (
          <section key={domain.id} className="session-factor-list__domain">
            <h4 className="session-factor-list__domain-title">
              {frameworkLabel(domain, "domain", i18n.language, t)}
            </h4>
            <ul className="session-factor-list__rows">
              {rows.map((a) => (
                <li key={a.subdomain_id} className="session-factor-row">
                  <span className="session-factor-row__name">
                    {labelFor(a.subdomain_id, a.name_en)}
                  </span>
                  <span
                    className={`session-factor-row__state session-factor-row__state--${a.state_kind}`}
                  >
                    <StateIcon
                      state={a.state_kind}
                      className="session-factor-row__swatch"
                    />
                    {t(
                      `framework.state.${
                        a.state_kind === "not-assessed"
                          ? "notAssessed"
                          : a.state_kind
                      }.descriptor`,
                    )}
                    {a.state_kind === "not-assessed" &&
                      a.not_assessed_reason_id && (
                        <span className="session-factor-row__reason">
                          {" · "}
                          {t(
                            `framework.notAssessedReason.${reasonKey(a.not_assessed_reason_id)}`,
                          )}
                        </span>
                      )}
                  </span>
                </li>
              ))}
            </ul>
          </section>
        );
      })}
    </div>
  );
}

function reasonKey(s: string): string {
  switch (s) {
    case "not-applicable":
      return "notApplicable";
    case "declined":
      return "declined";
    case "not-yet-assessed":
      return "notYetAssessed";
    default:
      return s;
  }
}

export default CycleHistoryStrip;
